/**
 * SE-6488: board-scoped player search + board-level sort over the Kanban
 * board's column data.
 *
 * This is the ONLY place the header's `Search players` input and `Sort` control
 * touch the board dataset. Both are applied to `columnsData` before it reaches
 * the renderers, so the SAME derivation feeds the Kanban columns and the Table
 * — the AC requires the search to hide non-matching cards "across all stages",
 * which is only true if the filter runs above the per-column components.
 *
 * Board search is intentionally CLIENT-side: `columnsData` is already fully
 * loaded in memory (the board fetches all cards to render its columns), so a
 * server round-trip would add latency and a loading state for a filter that can
 * be answered synchronously. This is distinct from the add-players panel's
 * search (`useAddPlayersSearch`), which queries the whole player universe and
 * therefore must hit the server.
 * @module features/player-kanban/utils/boardSearchSort
 */

import type { BoardSortState } from "@/features/player-kanban/constants/boardSort";
import type { ColumnData, Player } from "@/features/player-kanban/types/types";

/**
 * Parse a contract value string (e.g. "€12.5M", "-") into a comparable number.
 * Mirrors the per-column sort's parsing so the two controls order identically.
 * @param value - The display contract value.
 * @returns The numeric value, or 0 when unparseable.
 */
const contractValueOf = (value: string | undefined): number =>
  parseFloat((value ?? "").replace(/[^0-9.-]/g, "")) || 0;

/** A sortable field's comparator. */
type PlayerComparator = (a: Player, b: Player) => number;

/**
 * Comparator per sortable field.
 *
 * A `Record` lookup rather than a switch: adding a `BoardSortOption` becomes a
 * compile error here until its comparator exists, where a switch would have
 * silently fallen through to a no-op `default` and shipped an inert sort.
 */
const COMPARATOR_BY_FIELD: Readonly<
  Record<Exclude<BoardSortState["option"], "none">, PlayerComparator>
> = {
  name: (a, b) => a.name.localeCompare(b.name),
  age: (a, b) => (a.age || 0) - (b.age || 0),
  gpr: (a, b) => (a.gpr || 0) - (b.gpr || 0),
  contract: (a, b) =>
    contractValueOf(a.contractValue) - contractValueOf(b.contractValue),
};

/**
 * Does this player match the board search query?
 *
 * Matches on player name only, case-insensitively, as a substring. The AC
 * states "the board shows only cards matching that name", so name is the whole
 * contract — team/archetype matching would make the result set wider than the
 * criterion promises.
 * @param player - The candidate player.
 * @param normalizedQuery - Lowercased, trimmed query. Never empty.
 * @returns Whether the player matches.
 */
const matchesQuery = (player: Player, normalizedQuery: string): boolean =>
  player.name.toLowerCase().includes(normalizedQuery);

/**
 * Apply the board search query and board sort to every column.
 *
 * Column identity is preserved: a stage whose cards are all filtered out stays
 * in the board as an empty column rather than disappearing, so the board's
 * stage structure does not reflow while the user types. `count` is recomputed
 * from the surviving players so the column header badge stays truthful.
 * @param columns - The board's column data, or undefined while loading.
 * @param searchQuery - The raw search input value (may be empty/whitespace).
 * @param sort - The board-level sort selection.
 * @returns Columns with filtered + sorted players, or the input when nothing applies.
 */
export function applyBoardSearchAndSort<T extends ColumnData[] | undefined>(
  columns: T,
  searchQuery: string,
  sort: BoardSortState
): T {
  if (!columns) return columns;

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const hasQuery = normalizedQuery.length > 0;
  // Bound to a local so the narrowing survives into the comparator closure.
  const sortField = sort.option === "none" ? null : sort.option;

  // Identity fast-path: never clone the dataset when neither control is active,
  // so the default board render keeps its existing referential stability (and
  // the memoized column components below it do not re-render).
  if (!hasQuery && !sortField) return columns;

  const { direction } = sort;

  return columns.map(column => {
    const filtered = hasQuery
      ? column.players.filter(player => matchesQuery(player, normalizedQuery))
      : column.players;

    const compare = sortField ? COMPARATOR_BY_FIELD[sortField] : null;
    const ordered = compare
      ? [...filtered].sort((a, b) => {
          const comparison = compare(a, b);
          return direction === "asc" ? comparison : -comparison;
        })
      : filtered;

    return { ...column, players: ordered, count: ordered.length };
  }) as T;
}

/**
 * Total number of cards visible across all columns — used to render the
 * "no results" affordance without re-walking the dataset in the view.
 * @param columns - Column data (already filtered).
 * @returns The visible card count.
 */
export const totalVisibleCards = (columns: ColumnData[] | undefined): number =>
  (columns ?? []).reduce((sum, column) => sum + column.players.length, 0);

/**
 * Translate a drop index expressed against the RENDERED column into the
 * equivalent index in the FULL (unfiltered) column.
 *
 * Coordinate-derived drop indices are measured against what is on screen, but
 * a card's position is persisted against the whole column. With a search
 * active those two index spaces diverge — dropping "at visible slot 1" of a
 * 2-row filtered column must not persist as "slot 1" of a 30-card column.
 *
 * The visible slot is resolved through the IDENTITY of the card currently
 * occupying it: slot `i` means "immediately before visible player `i`", whose
 * true position is its index in the full column. Dropping past the last
 * visible row appends to the end of the full column.
 * @param fullPlayers - The column's complete player list.
 * @param visiblePlayers - The rendered subset, in rendered order.
 * @param visibleIndex - Insertion slot within the rendered subset.
 * @returns The equivalent insertion index in `fullPlayers`.
 */
export const toFullBoardIndex = (
  fullPlayers: readonly Player[],
  visiblePlayers: readonly Player[],
  visibleIndex: number
): number => {
  const anchor = visiblePlayers[visibleIndex];
  if (!anchor) return fullPlayers.length;

  const fullIndex = fullPlayers.findIndex(player => player.id === anchor.id);
  return fullIndex >= 0 ? fullIndex : fullPlayers.length;
};
